import { DateTime } from "luxon";

const TIMEZONE = import.meta.env.VITE_TIMEZONE;

export default class DateUtils {
  /*
   * Formats the given date in the selected timezone.
   */
  static formatInTimezone(date: Date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error("Invalid date provided.");
    }
    try {
      return DateTime.fromJSDate(date).setZone(TIMEZONE);
    } catch (error) {
      throw new Error("Invalid timezone provided.");
    }
  }

  /**
   * Returns the given date in the current timezone in ISO format (YYYY-MM-DD)
   * @param {Date} date - The date to format
   * @returns {string} The date in ISO format (YYYY-MM-DD)
   */
  static getISOinCurrentTimezone(date: Date): string {
    // Ensure the provided date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error("Invalid date provided.");
    }

    // Convert the provided date to Luxon's DateTime and format it
    return DateTime.fromJSDate(date).toISODate() as string;
  }

  /*
   * Returns the ISO date in a selected timezone string for the given date.
   */
  static getISODate(date: Date) {
    return this.formatInTimezone(date).toISODate();
  }

  /**
   * Converts a date string to a Date object in the current timezone
   * @param {string} dateString - The date string to convert
   * @returns {Date} The converted date in the current timezone
   */
  static stringToDateInCurrentTimezone(dateString: string): Date {
    const parsedDate = DateTime.fromISO(dateString, { zone: "local" });

    if (!parsedDate.isValid) {
      throw new Error("Invalid date string provided.");
    }

    return parsedDate.toJSDate();
  }

  static getHumanReadableDate(date: Date | string): string {
    if (typeof date === "string") {
      return this.formatInTimezone(new Date(date)).toLocaleString(DateTime.DATE_HUGE);
    }
    return this.formatInTimezone(date).toLocaleString(DateTime.DATE_HUGE);
  }

  /*
   * Returns the Human readable format of the time in the specific timezone
   * @param date - Date object or ISO date string
   */
  static getHumanReadableTime(date: Date | string): string {
    if (typeof date === "string") {
      return this.formatInTimezone(new Date(date)).toLocaleString(
        DateTime.TIME_SIMPLE
      );
    }
    return this.formatInTimezone(date).toLocaleString(DateTime.TIME_SIMPLE);
  }

  /*
   *Creates a date of specific zone and returns an ISO string
   */
  static createISODate(day: number, month: number, year: number) {
    const utcDate = DateTime.utc(year, month, day);

    // Convert to IST timezone (UTC+5:30)
    const istDate = utcDate.setZone(TIMEZONE);

    // Format the date as ISO format
    return istDate.toISO().split("T")[0];
  }
}
